import { FunctionComponent, useState } from "react";
import { Link } from "react-router-dom";

import { faReply } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components";

import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardRepliesItem, DashboardRepliesSection } from "../schema/dashboard";
import { formatRelativeTime, personRowFields } from "../utils/personRowFields";
import { DashboardBox } from "./DashboardBox";

interface RepliesBoxProps {
	mine: DashboardRepliesSection;
	company: DashboardRepliesSection;
}

type Scope = "mine" | "team";

const formatTotal = (total: number) => (total >= 1000 ? "1000+" : String(total));

const ReplyRow: FunctionComponent<{ row: DashboardRepliesItem }> = ({ row }) => {
	const fields = personRowFields(row);
	const inboxPath = `${ROUTE_CONFIG.MESSAGE_CENTER_INBOX}?listId=${fields.listId}&listPersonId=${fields.listPersonId}`;

	return (
		<Link
			className="group flex items-center gap-3 px-4 py-3 hover:bg-background-light"
			to={inboxPath}
		>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-text truncate">
					{fields.displayName}
					{fields.organization && (
						<span className="text-text-meta font-normal"> · {fields.organization}</span>
					)}
				</div>
				<div className="text-xs text-text-meta truncate">{fields.listName}</div>
			</div>
			<span className="text-[11px] text-text-meta w-12 text-right shrink-0">
				{formatRelativeTime(fields.updatedAtIso)}
			</span>
		</Link>
	);
};

const CountPill: FunctionComponent<{ count: number }> = ({ count }) => (
	<span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[11px] font-semibold bg-status-success-light text-status-success mr-1">
		{formatTotal(count)}
	</span>
);

export const RepliesBox: FunctionComponent<RepliesBoxProps> = ({ mine, company }) => {
	const [scope, setScope] = useState<Scope>("mine");
	const section = scope === "mine" ? mine : company;
	const items = section.items;

	return (
		<DashboardBox
			accent="green"
			dismissible={false}
			footer={
				items.length > 0 ? (
					<Link to={ROUTE_CONFIG.MESSAGE_CENTER_INBOX}>
						View all {formatTotal(section.total)} →
					</Link>
				) : undefined
			}
			headRight={
				<div className="inline-flex rounded-md border border-border-light p-0.5 mr-2">
					{(["mine", "team"] as const).map((s) => (
						<button
							key={s}
							className={classNames(
								"px-2.5 py-1 text-xs font-medium rounded capitalize",
								scope === s ? "bg-action-200 text-brand-light" : "text-text-meta"
							)}
							type="button"
							onClick={() => setScope(s)}
						>
							{s}
						</button>
					))}
				</div>
			}
			icon={<Icon className="w-4 h-4" icon={faReply} />}
			title={
				<>
					<CountPill count={section.total} />
					Replies need attention
				</>
			}
		>
			{items.length === 0 ? (
				<div className="px-4 py-6 text-center text-xs text-text-meta">
					{scope === "mine"
						? "No replies waiting for you right now."
						: "No replies for the team right now."}
				</div>
			) : (
				items.map((row) => <ReplyRow key={row.id} row={row} />)
			)}
		</DashboardBox>
	);
};
